import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { initializeUser } from '../reducers/userReducer'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import axios from 'axios'

const Testing = () => {
  const dispatch = useDispatch()
  const user = useSelector(state => state.user)
  const [idsText, setIdsText] = useState('')
  const [status, setStatus] = useState({ type: '', text: '' })
  const [loading, setLoading] = useState(false)
  const [pageText, setPageText] = useState('')
  const [parsed, setParsed] = useState(null)
  const [parseStatus, setParseStatus] = useState({ type: '', text: '' })
  const [saveStatus, setSaveStatus] = useState({ type: '', text: '' })
  const [filterStatus, setFilterStatus] = useState({ type: '', text: '' })
  const [averages, setAverages] = useState(null)
  const [includeTactics, setIncludeTactics] = useState([])
  const [excludeTactics, setExcludeTactics] = useState([])

  useEffect(() => {
    dispatch(initializeUser())
  }, [dispatch])

  const handleImport = async event => {
    event.preventDefault()
    setStatus({ type: '', text: '' })
    const trimmed = idsText.trim()
    if (!trimmed) {
      setStatus({ type: 'danger', text: 'Lisää vähintään yksi ottelutunnus.' })
      return
    }
    try {
      setLoading(true)
      const response = await axios.post('/api/matches/import', {
        idsText: trimmed,
      })
      const { idsProcessed, upserted, updated, failed } = response.data
      const failedCount = failed && failed.length ? failed.length : 0
      setStatus({
        type: 'success',
        text: `Käsitelty ${idsProcessed} ottelua. Lisätty: ${upserted}, päivitetty: ${updated}, epäonnistui: ${failedCount}.`,
      })
    } catch (error) {
      console.error('Import error:', error)
      setStatus({ type: 'danger', text: 'Tuonti epäonnistui.' })
    } finally {
      setLoading(false)
    }
  }

  const normalizeNumber = value => {
    if (value === null || value === undefined) return null
    const s = String(value).trim().replace(',', '.')
    const n = Number(s)
    return isNaN(n) ? null : n
  }

  const parsePageText = text => {
    const result = {
      matchId: null,
      homeWins: null,
      draws: null,
      awayWins: null,
      avgHomeGoals: null,
      avgAwayGoals: null,
    }

    // Match ID inside parentheses e.g. (39775002)
    const idMatch = text.match(/\((\d{8})\)/)
    if (idMatch) {
      result.matchId = Number(idMatch[1])
    }

    // Average goals line: "<home> Maaleja keskimäärin <away>"
    const avgRegex = /(\d+[.,]?\d*)\s+Maaleja\s+keskimäärin\s+(\d+[.,]?\d*)/i
    const avgMatch = text.match(avgRegex)
    if (avgMatch) {
      result.avgHomeGoals = normalizeNumber(avgMatch[1])
      result.avgAwayGoals = normalizeNumber(avgMatch[2])
    }

    // After header "Odotustuloksen erittely" find first three integers (can be on their own lines)
    const lines = text.split(/\r?\n/)
    const startIndex = lines.findIndex(l =>
      /Odotustuloksen\s+erittely/i.test(l)
    )
    if (startIndex !== -1) {
      const nums = []
      for (let i = startIndex + 1; i < lines.length && nums.length < 3; i++) {
        const m = lines[i].match(/^(\s*)(\d+)(\s*)$/)
        if (m) nums.push(Number(m[2]))
      }
      if (nums.length === 3) {
        result.homeWins = nums[0]
        result.draws = nums[1]
        result.awayWins = nums[2]
      }
    }

    return result
  }

  const handleParse = event => {
    event.preventDefault()
    setParseStatus({ type: '', text: '' })
    const trimmed = pageText.trim()
    if (!trimmed) {
      setParseStatus({ type: 'danger', text: 'Liitä sivun tekstit kenttään.' })
      setParsed(null)
      return
    }
    const res = parsePageText(trimmed)
    const missing =
      res.matchId === null ||
      res.matchId === undefined ||
      res.homeWins === null ||
      res.homeWins === undefined ||
      res.draws === null ||
      res.draws === undefined ||
      res.awayWins === null ||
      res.awayWins === undefined ||
      res.avgHomeGoals === null ||
      res.avgHomeGoals === undefined ||
      res.avgAwayGoals === null ||
      res.avgAwayGoals === undefined

    if (missing) {
      setParseStatus({
        type: 'warning',
        text: 'Kaikkia tietoja ei löytynyt. Tarkista teksti tai formaatti.',
      })
    } else {
      setParseStatus({
        type: 'success',
        text: 'Tiedot parsittu onnistuneesti.',
      })
      // Auto-save simulations after successful parse
      handleSaveSimulations(res)
    }
    setParsed(res)
  }

  const handleSaveSimulations = async payloadOverride => {
    setSaveStatus({ type: '', text: '' })
    const src = payloadOverride || parsed
    if (!src || src.matchId === null || src.matchId === undefined) {
      setSaveStatus({ type: 'danger', text: 'OtteluID puuttuu.' })
      return
    }
    try {
      const body = {
        ID: src.matchId,
        HomeWins: src.homeWins,
        Draws: src.draws,
        AwayWins: src.awayWins,
        AvgHomeGoals: src.avgHomeGoals,
        AvgAwayGoals: src.avgAwayGoals,
      }
      const resp = await axios.post(
        `/api/matches/${src.matchId}/simulations`,
        body
      )
      if (resp.status === 200) {
        setSaveStatus({ type: 'success', text: 'Simulaatiot tallennettu.' })
        // Clear the pasted page text after successful save
        setPageText('')
      } else {
        setSaveStatus({ type: 'danger', text: 'Tallennus epäonnistui.' })
      }
    } catch (err) {
      console.error('Save simulations error:', err)
      setSaveStatus({ type: 'danger', text: 'Tallennus epäonnistui.' })
    } finally {
      // no-op
    }
  }

  const computeAverages = matches => {
    let count = 0
    let sumHomeWins = 0
    let sumDraws = 0
    let sumAwayWins = 0
    let sumAvgHomeGoals = 0
    let sumAvgAwayGoals = 0
    let maxHomeWinsMatch = null
    let maxAwayWinsMatch = null
    let maxHomeWins = -Infinity
    let maxAwayWins = -Infinity

    matches.forEach(m => {
      const s = m.Simulations
      if (
        s &&
        Number.isFinite(Number(s.HomeWins)) &&
        Number.isFinite(Number(s.Draws)) &&
        Number.isFinite(Number(s.AwayWins)) &&
        Number.isFinite(Number(s.AvgHomeGoals)) &&
        Number.isFinite(Number(s.AvgAwayGoals))
      ) {
        count += 1
        const hw = Number(s.HomeWins)
        const aw = Number(s.AwayWins)
        sumHomeWins += Number(s.HomeWins)
        sumDraws += Number(s.Draws)
        sumAwayWins += Number(s.AwayWins)
        sumAvgHomeGoals += Number(s.AvgHomeGoals)
        sumAvgAwayGoals += Number(s.AvgAwayGoals)
        if (hw > maxHomeWins) {
          maxHomeWins = hw
          maxHomeWinsMatch = m
        }
        if (aw > maxAwayWins) {
          maxAwayWins = aw
          maxAwayWinsMatch = m
        }
      }
    })
    if (count === 0) return null
    return {
      count,
      HomeWins: +(sumHomeWins / count).toFixed(2),
      Draws: +(sumDraws / count).toFixed(2),
      AwayWins: +(sumAwayWins / count).toFixed(2),
      AvgHomeGoals: +(sumAvgHomeGoals / count).toFixed(2),
      AvgAwayGoals: +(sumAvgAwayGoals / count).toFixed(2),
      maxHomeWinsMatch,
      maxAwayWinsMatch,
    }
  }

  const handleFetchFiltered = async () => {
    try {
      setFilterStatus({ type: '', text: '' })
      const includeParam = includeTactics.join(',')
      const excludeParam = excludeTactics.join(',')
      const url = `/api/matches/tactics?include=${encodeURIComponent(
        includeParam
      )}&exclude=${encodeURIComponent(excludeParam)}`
      const resp = await axios.get(url)
      const avg = computeAverages(resp.data)
      setAverages(avg)
      setFilterStatus({ type: 'success', text: 'Suodatetut ottelut haettu.' })
    } catch (err) {
      console.error('Fetch filtered matches error:', err)
      setFilterStatus({ type: 'danger', text: 'Haku epäonnistui.' })
    }
  }
  return (
    <Card bg="dark" text="light" className="mb-3">
      <Card.Header>Testing</Card.Header>
      <Card.Body>
        <Card.Title>Käyttäjän tunnistus</Card.Title>
        <Card.Text>
          {user ? (
            <span>
              Käyttäjä tunnistettu: {user.username || user.oauth_token}
            </span>
          ) : (
            <span>Ei kirjautunutta käyttäjää</span>
          )}
        </Card.Text>

        <Form onSubmit={handleImport}>
          <Form.Group className="mb-3" controlId="matchIdsTextarea">
            <Form.Label>
              Liitä ottelutunnukset (Excelistä tai tekstistä)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Esim: 1234567, 2345678, 3456789"
              value={idsText}
              onChange={e => setIdsText(e.target.value)}
              disabled={loading}
            />
            <Form.Text className="text-muted">
              Numerot poimitaan automaattisesti tekstistä.
            </Form.Text>
          </Form.Group>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Tuodaan...' : 'Tuo ottelut'}
          </Button>
        </Form>

        {status.text ? (
          <Alert variant={status.type} className="mt-3">
            {status.text}
          </Alert>
        ) : null}

        <hr className="mt-4" />
        <Card.Title>Simulaatiosivun tekstien tuonti</Card.Title>
        <Form onSubmit={handleParse}>
          <Form.Group className="mb-3" controlId="pageTextTextarea">
            <Form.Label>Liitä koko sivun teksti</Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              placeholder="Liitä tähän koko sivun teksti..."
              value={pageText}
              onChange={e => setPageText(e.target.value)}
            />
            <Form.Text className="text-muted">
              Poimitaan otteluID, kotivoitot, tasapelit, vierasvoitot sekä
              keskimääräiset maalimäärät.
            </Form.Text>
          </Form.Group>
          <Button variant="secondary" type="submit">
            Parsii tiedot
          </Button>
        </Form>
        {parseStatus.text ? (
          <Alert variant={parseStatus.type} className="mt-3">
            {parseStatus.text}
          </Alert>
        ) : null}

        {parsed ? (
          <div className="mt-3">
            <Card.Subtitle className="mb-2">Parsittu data</Card.Subtitle>
            <div>
              OtteluID:{' '}
              {parsed.matchId !== null && parsed.matchId !== undefined
                ? parsed.matchId
                : '-'}
            </div>
            <div>
              Odotustuloksen erittely: koti{' '}
              {parsed.homeWins !== null && parsed.homeWins !== undefined
                ? parsed.homeWins
                : '-'}
              , tasapeli{' '}
              {parsed.draws !== null && parsed.draws !== undefined
                ? parsed.draws
                : '-'}
              , vieras{' '}
              {parsed.awayWins !== null && parsed.awayWins !== undefined
                ? parsed.awayWins
                : '-'}
            </div>
            <div>
              Keskim. maaleja: koti{' '}
              {parsed.avgHomeGoals !== null && parsed.avgHomeGoals !== undefined
                ? parsed.avgHomeGoals
                : '-'}
              , vieras{' '}
              {parsed.avgAwayGoals !== null && parsed.avgAwayGoals !== undefined
                ? parsed.avgAwayGoals
                : '-'}
            </div>
            {saveStatus.text ? (
              <Alert variant={saveStatus.type} className="mt-2">
                {saveStatus.text}
              </Alert>
            ) : null}
          </div>
        ) : null}
        <hr className="mt-4" />
        <Card.Title>Taktiikkasuodatus (EventTypeID 331-336)</Card.Title>
        <div className="mb-2">
          <Form.Label>Halutut taktiikat</Form.Label>
          {[
            { code: '331', label: 'Pressing' },
            { code: '332', label: 'Counter-attacking' },
            { code: '333', label: 'Attack in middle' },
            { code: '334', label: 'Attack on wings' },
            { code: '335', label: 'Play creatively' },
            { code: '336', label: 'Long shots' },
          ].map(opt => (
            <Form.Check
              inline
              key={`inc-${opt.code}`}
              type="checkbox"
              id={`inc-${opt.code}`}
              label={`${opt.code} ${opt.label}`}
              checked={includeTactics.includes(opt.code)}
              onChange={e => {
                setIncludeTactics(prev => {
                  if (e.target.checked) return [...prev, opt.code]
                  return prev.filter(c => c !== opt.code)
                })
              }}
            />
          ))}
        </div>
        <div className="mb-2">
          <Form.Label>Ei halutut taktiikat</Form.Label>
          {[
            { code: '331', label: 'Pressing' },
            { code: '332', label: 'Counter-attacking' },
            { code: '333', label: 'Attack in middle' },
            { code: '334', label: 'Attack on wings' },
            { code: '335', label: 'Play creatively' },
            { code: '336', label: 'Long shots' },
          ].map(opt => (
            <Form.Check
              inline
              key={`exc-${opt.code}`}
              type="checkbox"
              id={`exc-${opt.code}`}
              label={`${opt.code} ${opt.label}`}
              checked={excludeTactics.includes(opt.code)}
              onChange={e => {
                setExcludeTactics(prev => {
                  if (e.target.checked) return [...prev, opt.code]
                  return prev.filter(c => c !== opt.code)
                })
              }}
            />
          ))}
        </div>
        <Button variant="info" onClick={handleFetchFiltered} className="mb-2">
          Hae suodatetut ottelut
        </Button>
        {filterStatus.text ? (
          <Alert variant={filterStatus.type}>{filterStatus.text}</Alert>
        ) : null}
        {averages ? (
          <div className="mt-2">
            <Card.Subtitle className="mb-2">
              Simulaatioiden keskiarvot ({averages.count} ottelua)
            </Card.Subtitle>
            <div>HomeWins: {averages.HomeWins}</div>
            <div>Draws: {averages.Draws}</div>
            <div>AwayWins: {averages.AwayWins}</div>
            <div>AvgHomeGoals: {averages.AvgHomeGoals}</div>
            <div>AvgAwayGoals: {averages.AvgAwayGoals}</div>
            <hr className="my-2" />
            <Card.Subtitle className="mb-2 mt-2">Ääripäät</Card.Subtitle>
            {averages.maxHomeWinsMatch ? (
              <div>
                <strong>Eniten kotivoittoja:</strong> MatchID{' '}
                {averages.maxHomeWinsMatch.MatchID} -{' '}
                {averages.maxHomeWinsMatch.Simulations.HomeWins}-
                {averages.maxHomeWinsMatch.Simulations.Draws}-
                {averages.maxHomeWinsMatch.Simulations.AwayWins}
              </div>
            ) : null}
            {averages.maxAwayWinsMatch ? (
              <div>
                <strong>Eniten vierasvoittoja:</strong> MatchID{' '}
                {averages.maxAwayWinsMatch.MatchID} -{' '}
                {averages.maxAwayWinsMatch.Simulations.HomeWins}-
                {averages.maxAwayWinsMatch.Simulations.Draws}-
                {averages.maxAwayWinsMatch.Simulations.AwayWins}
              </div>
            ) : null}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  )
}

export default Testing
